import type { BankFileSynthesis, CapacityOperands } from "@hakili/contracts";
import { formatDate, formatMoney, t } from "@hakili/i18n";
import { Button, Card } from "@hakili/ui";
import { FileText, X } from "lucide-react";
import { useState, type ReactElement, type ReactNode } from "react";

import type { AnalysisPointView, AnalysisView } from "./analyse.js";
import type { ComposerFactsView } from "./composer.js";
import { asSentence } from "../sentence.js";
import { fill } from "./dossiers.js";
import { OfferReadFailure } from "./offer-read-failure.js";

/**
 * `A-1b-2a` (D3 R-5, WF-BNK-04 steps 4-5, D2-copy « Synthèse »; the architect's ruling on M0): **the synthesis of the dossier, behind its
 * button, at every stage.** The drawing's four cards (« Le client », « La demande », « Capacité et garanties », « Financement : ce qui entre
 * dans le prêt »), every figure a field of a door and nothing computed in the browser:
 *
 * - the capacity operands (`underwriting`, the quotité point's evidence) for the net salary and the commitments , **the same source the bay's
 *   calculation sheet prints** (`quotite-sheet.tsx`), so two screens never disagree (R1) , and for the quotité cessible and the part used;
 * - the file's own read (`GET /api/v1/bank/files/{id}`): the vehicle, the deposit's percent, the term, the financed amount, the available capacity;
 * - the offer door's facts (`GET /api/v1/bank/files/{id}/offer`, answered at « nouveau » too): the vehicle's year, the expertised value, the two fees;
 * - the points' compared strings, as the door words them: the stored quotité, the LTV and its ceiling, the financed warranty.
 *
 * **A figure with no field on the wire is a dash (R4).** `A-1b-2b` binds the employer, the seniority, the contract, the price, the apport in
 * francs, the seller and the warranty's settlement to the file's typed `synthesis`: a fact the file does not hold is the dash with the server's
 * own `notRead.words` through `asSentence`, never a 0; the seniority is `declaredSeniorityMonths` as given (no conversion), else the hire date.
 * Hakili's file fee reads « avant le rendez-vous de signature », the bank's « à la signature »
 * (the F ruling, `ADDENDUM-2026-09-18-fees.md` (a)). The drawing's « Dossier tab » is in no record and is not drawn.
 */
export interface SyntheseFile {
  readonly vehicleLabel: string | null;
  readonly depositPct: number | null;
  readonly durationMonths: number | null;
  readonly amountFinanced: number;
  /** The file's own `figures.availableCapacityAmount`; `null` where the door gives none. */
  readonly availableCapacityAmount?: number | null;
  /** `A-1b-2b`: the file's own `synthesis` (SYNTHESIS-FIELDS), or `null` where the door sends none; a fact it does not hold is the dash with the server's cause. */
  readonly synthesis?: BankFileSynthesis | null;
}

const DASH = t("common.valeur.tiret");

const pointOf = (analysis: AnalysisView | null, code: string): AnalysisPointView | undefined =>
  analysis?.points.find((one) => one.code === code);

const operandsOf = (analysis: AnalysisView | null): CapacityOperands | null => {
  const evidence = pointOf(analysis, "quotiteCessible")?.evidence;
  return evidence?.kind === "figures" ? evidence.operands : null;
};

const comparedOf = (analysis: AnalysisView | null, code: string, label: string): string | null => {
  const evidence = pointOf(analysis, code)?.evidence;
  if (evidence?.kind !== "figures") return null;
  const value = evidence.compared[label];
  return value === undefined || value === null ? null : String(value);
};

const money = (value: number | null | undefined): string =>
  value === null || value === undefined ? DASH : formatMoney(value);

/** `C2`: a monthly figure: « N FCFA / mois », « aucun » for a real zero (D2 over 06 §6.5.2, both kept), the dash for an unknown. */
const monthly = (value: number | null): string =>
  value === null
    ? DASH
    : value === 0
      ? t("bank.synthese.aucun")
      : fill("bank.synthese.parMois", { montant: formatMoney(value) });

const tenths = (value: number): string => {
  const [whole = "0", fraction = "0"] = String(value).split(".");
  return `${whole},${fraction}`;
};

const termOf = (operands: CapacityOperands | null): string | null =>
  operands?.termMonths === null || operands?.termBasis === null || operands === null
    ? null
    : fill("bank.briefing.dureeMois", {
        n: String(operands.termMonths),
        base: t(
          operands.termBasis === "asked" ? "bank.briefing.dureeDemandee" : "bank.briefing.dureeMaximale",
        ),
      });

/** The server's cause for an absent fact, as a sentence; `null` where the fact is present or the door sent none. */
/** D2 frame 51, « Ce que Hakili avait lu »: the note, the share of the assignable quotité used (at the asked term) and the LTV, as the run's own figures. */
export function closReading(analysis: AnalysisView | null): {
  readonly note: string | null;
  readonly part: string | null;
  readonly term: string | null;
  readonly ltv: string | null;
} {
  const operands = operandsOf(analysis);
  const grade = analysis?.grade ?? null;
  const usedPct = operands?.capacityUsedPct ?? null;
  return {
    note:
      grade === null
        ? null
        : grade.incomplete !== null
          ? t("bank.clos.noteIncomplete")
          : grade.grade === null || grade.average === null
            ? null
            : `${grade.grade} · ${tenths(grade.average)}`,
    part: usedPct === null ? null : fill("bank.briefing.pct", { n: tenths(usedPct) }),
    term: termOf(operands),
    ltv: comparedOf(analysis, "prixValeurExpertisee", t("bank.briefing.cleLtv")),
  };
}

const causeOf = (notRead: { readonly words: string } | null | undefined): string | null =>
  notRead === null || notRead === undefined ? null : asSentence(notRead.words);

function Row(props: {
  readonly id: string;
  readonly label: string;
  readonly value: ReactNode;
  /** The sentence under an absent value (`notRead.words`); never drawn beside a present one. */
  readonly cause?: string | null;
  /** A line under a present value (the apport's basis, as the server words it). */
  readonly note?: string | null;
  /** A value that says nothing was declared (« non déclarée »): regular weight, not the figures' medium. */
  readonly plain?: boolean;
}): ReactElement {
  return (
    <div className="py-1.5 text-sm">
      <div className="flex items-baseline justify-between gap-4">
        <dt className="text-gray-600">{props.label}</dt>
        <dd
          className={`whitespace-nowrap text-right tabular-nums text-gray-900 ${props.plain === true ? "font-normal" : "font-medium"}`}
          data-testid={`bank-synthese-${props.id}`}
        >
          {props.value}
        </dd>
      </div>
      {props.note === undefined || props.note === null ? null : (
        <p
          className="mt-0.5 text-right text-sm text-[color:var(--hk-text-2)]"
          data-testid={`bank-synthese-${props.id}-base`}
        >
          {props.note}
        </p>
      )}
      {props.cause === undefined || props.cause === null ? null : (
        <p className="mt-0.5 text-sm text-gray-700" data-testid={`bank-synthese-${props.id}-cause`}>
          {props.cause}
        </p>
      )}
    </div>
  );
}

/** Spelled with literal catalogue keys, as the buyer's declaration does (the catalogue's guard reads keys it can SEE); a Map, so no new French name is an identifier. */
const CONTRACT_WORDS = new Map<BankFileSynthesis["employments"]["items"][number]["contractType"], string>([
  ["fonctionnaireTitulaire", t("bank.synthese.contratFonctionnaireTitulaire")],
  ["cdi", t("bank.synthese.contratCdi")],
  ["cdd", t("bank.synthese.contratCdd")],
  ["contractuelEtat", t("bank.synthese.contratContractuelEtat")],
  ["autre", t("bank.synthese.contratAutre")],
]);

/** F-FLOOR-2's rule for a date in words (« 1er septembre 2023 »): the day, the month and the year are one unit (local; shared formatters are FRENCH-SPACING's). */
const DATE_IN_WORDS = /(\d{1,2}|1er) (\p{Ll}+) (\d{4})/gu;
const bindDate = (words: string): string => words.replace(DATE_IN_WORDS, "$1\u00A0$2\u00A0$3");

/** The seniority as the file declares it: the months as given, else « embauché le {date} », else the dash (the cause is the caller's). */
const seniorityOf = (item: BankFileSynthesis["employments"]["items"][number]): string | null =>
  item.declaredSeniorityMonths !== null
    ? fill("bank.synthese.ancienneteMois", { n: String(item.declaredSeniorityMonths) })
    : item.dateOfHire !== null
      ? fill("bank.synthese.embaucheLe", { date: bindDate(formatDate(new Date(item.dateOfHire))) })
      : null;

/** One line per declared employment, in the server's order; a `plain` line says nothing was declared (regular weight). */
function Lines(props: {
  readonly id: string;
  readonly lines: readonly { readonly text: string; readonly plain?: boolean }[];
}): ReactElement {
  return (
    <>
      {props.lines.map((line, index) => (
        <span
          key={`${props.id}-${String(index)}`}
          className={`block whitespace-normal ${line.plain === true ? "font-normal" : ""}`}
          data-testid={`bank-synthese-${props.id}-${String(index)}`}
        >
          {line.text}
        </span>
      ))}
    </>
  );
}

type Employment = BankFileSynthesis["employments"]["items"][number];

/** The seniority line: the months as given, « embauché le {date} », or « non déclarée » (plain: no dash, no stop). */
const seniorityLine = (one: Employment): { readonly text: string; readonly plain?: boolean } => {
  const words = seniorityOf(one);
  return words === null ? { text: t("bank.synthese.nonDeclaree"), plain: true } : { text: words };
};

/** With two employments or more: the employer's name as the block's line, « Ancienneté » and « Contrat » under it (a term and its nested list, valid in the card's `dl`). */
function EmploymentBlock(props: { readonly index: number; readonly one: Employment }): ReactElement {
  const { index, one } = props;
  const seniority = seniorityLine(one);
  return (
    <div data-testid={`bank-synthese-client-bloc-${String(index)}`}>
      <dt
        className="pt-2 text-sm font-semibold text-gray-900"
        data-testid={`bank-synthese-client-employeur-${String(index)}`}
      >
        {one.employer}
      </dt>
      <dd>
        <dl className="divide-y divide-gray-100">
          <Row
            id={`client-anciennete-${String(index)}`}
            label={t("bank.synthese.anciennete")}
            value={<span className="block whitespace-normal">{seniority.text}</span>}
            plain={seniority.plain === true}
          />
          <Row
            id={`client-contrat-${String(index)}`}
            label={t("bank.synthese.contrat")}
            value={CONTRACT_WORDS.get(one.contractType) ?? DASH}
          />
        </dl>
      </dd>
    </div>
  );
}

function Carte(props: {
  readonly id: string;
  readonly title: string;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <div data-testid={`bank-synthese-carte-${props.id}`}>
      <Card className="h-full p-4">
        <h2 className="mb-1 text-sm font-semibold text-gray-900">{props.title}</h2>
        <dl className="divide-y divide-gray-100">{props.children}</dl>
      </Card>
    </div>
  );
}

export function Synthese(props: {
  readonly file: SyntheseFile;
  readonly analysis: AnalysisView | null;
  readonly facts: ComposerFactsView | null;
  /** `SYNTHESE-READ-FAILURE`: the offer read FAILED (not late, not empty): its figures are withheld under a notice and « Réessayer » reads again. */
  readonly offerFailed?: boolean;
  readonly onRetry?: () => void;
  /** From « revue » on the notice stands above the stage's panels (`App.tsx`); the synthesis then withholds its rows without a second notice. */
  readonly offerNoticeAbove?: boolean;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const { file, analysis, facts } = props;
  const offerFailed = props.offerFailed === true;
  const operands = operandsOf(analysis);
  const year = offerFailed ? null : (facts?.vehicleYear ?? null);
  const sharePct = operands?.assignableSharePct ?? null;
  const usedPct = operands?.capacityUsedPct ?? null;
  const term = termOf(operands);
  const ltv = comparedOf(analysis, "prixValeurExpertisee", t("bank.briefing.cleLtv"));
  const ltvCap = comparedOf(analysis, "prixValeurExpertisee", t("bank.briefing.clePlafondLtv"));
  const stored = comparedOf(analysis, "quotiteCessible", t("bank.feuille.quotite"));
  const warranty = comparedOf(analysis, "prixValeurExpertisee", t("bank.synthese.cleGarantie"));
  const synthesis = file.synthesis ?? null;
  const employments = synthesis?.employments ?? null;
  const items = employments?.items ?? [];
  const employmentCause = causeOf(employments?.notRead);
  const settlement = synthesis?.warrantySettlement.value ?? null;
  // `WARRANTY-SUBSCRIBED`: « non souscrite » only on the server's own words, `subscribed === false` while nothing is decided (`state === "none"`);
  // `null` (no retained offer, no offer of this bank) and `true` print no state word.
  const notSubscribed =
    synthesis?.warrantySettlement.subscribed === false && synthesis.warrantySettlement.state === "none";
  const feeNote = (own: string): string =>
    facts?.feeFinancingRequested === true ? t("bank.synthese.fraisFinances") : own;
  const fee = (amount: number | undefined, own: string): string =>
    amount === undefined
      ? DASH
      : fill("bank.synthese.montantNote", { montant: formatMoney(amount), note: feeNote(own) });
  return (
    <section className="mb-4" data-testid="bank-synthese-zone">
      <Button
        variant="ghost"
        size="sm"
        className="inline-flex items-center gap-2 whitespace-nowrap"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
        data-testid="bank-synthese-bouton"
      >
        {open ? (
          <X className="h-4 w-4" aria-hidden="true" />
        ) : (
          <FileText className="h-4 w-4" aria-hidden="true" />
        )}
        {open ? t("bank.synthese.masquer") : t("bank.synthese.afficher")}
      </Button>
      {open ? (
        <div className="mt-3 space-y-4" data-testid="bank-synthese">
          {offerFailed && props.offerNoticeAbove !== true ? (
            <OfferReadFailure
              onRetry={
                props.onRetry ??
                (() => {
                  /* no retry wired: the notice still says so */
                })
              }
            />
          ) : null}
          <div className="grid grid-cols-3 gap-4 max-[1099px]:grid-cols-1">
            <Carte id="client" title={t("bank.synthese.client")}>
              {items.length >= 2 ? (
                items.map((one, index) => (
                  <EmploymentBlock key={`${one.employer}-${String(index)}`} index={index} one={one} />
                ))
              ) : (
                <>
                  <Row
                    id="client-employeur"
                    label={t("bank.synthese.employeur")}
                    value={
                      items.length === 0 ? (
                        DASH
                      ) : (
                        <Lines id="client-employeur" lines={items.map((one) => ({ text: one.employer }))} />
                      )
                    }
                    cause={items.length === 0 ? employmentCause : null}
                  />
                  <Row
                    id="client-anciennete"
                    label={t("bank.synthese.anciennete")}
                    value={
                      items.length === 0 ? (
                        DASH
                      ) : (
                        <Lines id="client-anciennete" lines={items.map(seniorityLine)} />
                      )
                    }
                  />
                  <Row
                    id="client-contrat"
                    label={t("bank.synthese.contrat")}
                    value={
                      items.length === 0 ? (
                        DASH
                      ) : (
                        <Lines
                          id="client-contrat"
                          lines={items.map((one) => ({ text: CONTRACT_WORDS.get(one.contractType) ?? DASH }))}
                        />
                      )
                    }
                  />
                </>
              )}
              <Row
                id="client-salaire"
                label={t("bank.synthese.salaire")}
                value={money(operands?.retainedNetAmount)}
              />
              <Row
                id="client-engagements"
                label={t("bank.synthese.engagements")}
                value={monthly(operands?.retainedCommitmentsAmount ?? null)}
              />
              {employments?.source === "live" && employments.note !== null ? (
                <p className="py-1.5 text-sm text-gray-700" data-testid="bank-synthese-client-note">
                  {asSentence(employments.note)}
                </p>
              ) : null}
            </Carte>
            <Carte id="demande" title={t("bank.synthese.demande")}>
              <Row
                id="demande-vehicule"
                label={t("bank.synthese.vehicule")}
                value={
                  file.vehicleLabel === null
                    ? DASH
                    : year === null
                      ? file.vehicleLabel
                      : `${file.vehicleLabel} ${String(year)}`
                }
              />
              <Row
                id="demande-prix"
                label={t("bank.synthese.prix")}
                value={money(synthesis?.vehiclePrice.amount)}
                cause={
                  synthesis?.vehiclePrice.amount === null ? causeOf(synthesis.vehiclePrice.notRead) : null
                }
              />
              <Row
                id="demande-apport"
                label={t("bank.synthese.apport")}
                value={
                  file.depositPct === null ? DASH : fill("bank.briefing.pct", { n: String(file.depositPct) })
                }
              />
              <Row
                id="demande-apport-francs"
                label={t("bank.synthese.apportFrancs")}
                value={money(synthesis?.apport.amount)}
                note={synthesis?.apport.amount === null ? null : (synthesis?.apport.basis ?? null)}
                cause={synthesis?.apport.amount === null ? causeOf(synthesis.apport.notRead) : null}
              />
              <Row
                id="demande-finance"
                label={t("bank.synthese.finance")}
                value={money(file.amountFinanced)}
              />
              <Row
                id="demande-duree"
                label={t("bank.synthese.dureeDemandee")}
                value={
                  file.durationMonths === null
                    ? DASH
                    : fill("bank.dossier.dureeMois", { duree: String(file.durationMonths) })
                }
              />
              <Row
                id="demande-vendeur"
                label={t("bank.synthese.vendeur")}
                value={synthesis?.seller.name ?? DASH}
                cause={synthesis?.seller.name === null ? causeOf(synthesis.seller.notRead) : null}
              />
            </Carte>
            <Carte id="capacite" title={t("bank.synthese.capacite")}>
              <Row
                id="capacite-cessible"
                label={
                  sharePct === null
                    ? t("bank.synthese.cessibleSansPart")
                    : fill("bank.synthese.cessible", {
                        pct: String(sharePct).replace(".", ","),
                      })
                }
                value={money(operands?.assignableShareAmount)}
              />
              <Row
                id="capacite-disponible"
                label={t("bank.synthese.disponible")}
                value={money(file.availableCapacityAmount)}
              />
              <Row
                id="capacite-stockee"
                label={
                  term === null ? t("bank.synthese.quotite") : fill("bank.synthese.quotiteA", { duree: term })
                }
                value={stored ?? DASH}
              />
              <Row
                id="capacite-utilisee"
                label={
                  term === null
                    ? t("bank.briefing.tuileQuotite")
                    : fill("bank.synthese.partUtiliseeA", { duree: term })
                }
                value={usedPct === null ? DASH : fill("bank.briefing.pct", { n: tenths(usedPct) })}
              />
              {offerFailed ? null : (
                <Row
                  id="capacite-expertisee"
                  label={t("bank.synthese.valeurExpertisee")}
                  value={money(facts?.expertisedValueAmount)}
                />
              )}
              <Row
                id="capacite-ltv"
                label={t("bank.briefing.tuileLtv")}
                value={
                  ltv === null
                    ? DASH
                    : ltvCap === null
                      ? ltv
                      : fill("bank.synthese.ltvPlafond", { ltv, plafond: ltvCap })
                }
              />
              <Row
                id="capacite-garantie"
                label={t("bank.synthese.garantie")}
                value={
                  notSubscribed
                    ? warranty === null
                      ? t("bank.synthese.garantieNonSouscrite")
                      : fill("bank.synthese.garantieNonSouscriteMontant", { montant: warranty })
                    : (warranty ?? DASH)
                }
              />
              {settlement === null ? null : (
                <Row
                  id="capacite-garantie-reglement"
                  label={t("bank.synthese.garantieReglement")}
                  value={
                    settlement === "comptant"
                      ? t("bank.synthese.garantieComptant")
                      : t("bank.synthese.garantieRetiree")
                  }
                />
              )}
            </Carte>
          </div>
          <Carte id="financement" title={t("bank.synthese.financement")}>
            <Row
              id="financement-capital"
              label={t("bank.synthese.capital")}
              value={money(file.amountFinanced)}
            />
            {offerFailed ? null : (
              <>
                <Row
                  id="financement-banque"
                  label={t("bank.signature.fraisBanque")}
                  value={fee(facts?.bankFeeAmount, t("bank.synthese.fraisBanqueMoment"))}
                />
                <Row
                  id="financement-hakili"
                  label={t("bank.signature.fraisHakili")}
                  value={fee(facts?.hakiliFeeAmount, t("bank.synthese.fraisHakiliMoment"))}
                />
              </>
            )}
          </Carte>
        </div>
      ) : null}
    </section>
  );
}
